import React from 'react';

import { Link } from 'react-router-dom';

import { formatPhoneNumber } from '../../../util/formatters';
import { HelpScout } from '../../../util/helpscout-beacon';

const knownSolutions = {
  retryMessage: 'Try sending the message again',
  confirmPhoneNumber: (phoneNumber?: string) =>
    `Confirm the phone number is correct ${formatPhoneNumber(phoneNumber) ?? ''}`,
  tryEmailConfirmPhone:
    "Try emailing the recipient to confirm the recipient's phone is on and has signal",
  tryEmailLandline: 'Try emailing the recipient to confirm this phone number is not a landline',
  tryEmailResubscribe: 'Try emailing the recipient and ask them to resubscribe by texting "START"',
  updateLandline: 'Update the phone number to a cell phone number and re-send your message',
  checkEIN: (
    <Link to="/account/carrier-registration" className="typo-link">
      Check for registration errors
    </Link>
  ),
  sendADifferentMessage: 'Try sending a shorter message without links',
  /** This will always be included */
  contactSupport: (
    <div>
      <span
        className="typo-link"
        role="presentation"
        onClick={() => {
          HelpScout.beacon.navigate('/ask/message');
          HelpScout.beacon.prefillText('Message Not Delivered');
        }}
      >
        Contact Support
      </span>
    </div>
  ),
} as const;

type DetailedTwilioError = {
  description: string;
  twilioErrorPageLink: string;
  solutions: React.ReactNode[];
};

/**
 * Customer facing versions of Twilio error codes originally defined in this confluence page
 *  https://teamengine.atlassian.net/l/cp/Uogkw2Qt
 */
export const getDetailedTwilioError = (
  errorCode: string,
  { phoneNumber }: { phoneNumber?: string },
): DetailedTwilioError => {
  const unknownError = {
    description: '',
    solutions: [
      knownSolutions.retryMessage,
      knownSolutions.sendADifferentMessage,
      knownSolutions.confirmPhoneNumber(phoneNumber),
      knownSolutions.tryEmailConfirmPhone,
    ],
  } as const;

  const unreachableDestinationHandsetError = {
    description:
      'The cell phone you are trying to message is most likely turned off or does not have service at this time.',
    solutions: [
      knownSolutions.retryMessage,
      knownSolutions.confirmPhoneNumber(phoneNumber),
      knownSolutions.tryEmailConfirmPhone,
    ],
  };

  const carrierFilteringError = {
    description:
      "Cell phone carriers (AT&T, Verizon, T-Mobile, etc) in this exchange have blocked this message from being sent. This is often because there's an error in cell phone carrier registration.",
    solutions: [knownSolutions.checkEIN, knownSolutions.retryMessage],
  };

  const landlineError = {
    description: 'The phone you are trying to message is a landline.',
    solutions: [knownSolutions.tryEmailLandline, knownSolutions.updateLandline],
  };

  const unsubscribedRecipientError = {
    description:
      'The phone you are trying to message has opted out of receiving messages from this phone number.',
    solutions: [knownSolutions.tryEmailResubscribe],
  };

  const detailedErrorCodeDictionary: {
    [errorCode: string]: Omit<DetailedTwilioError, 'twilioErrorPageLink'>;
  } = {
    '30003': unreachableDestinationHandsetError,
    '30005': unreachableDestinationHandsetError,
    '30006': landlineError,
    '30032': carrierFilteringError,
    '30007': carrierFilteringError,
    '21610': unsubscribedRecipientError,
  };

  const { description, solutions } = detailedErrorCodeDictionary[errorCode] || unknownError;

  return {
    description,
    solutions: [...solutions, knownSolutions.contactSupport],
    // Every error code has help docs located with the same url structure
    // The full list of error codes is here https://www.twilio.com/docs/api/errors#3-anchor
    twilioErrorPageLink: `https://www.twilio.com/docs/api/errors/${errorCode}`,
  };
};
